import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Input, Form, DatePicker, Dropdown, Select, Spin } from 'antd';
import { MoreOutlined, FilterOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { createTransaction, deleteTransaction, editTransaction, getAllTransactions, getMembersData, getWorksByTransactionsData } from '../../app/api';
import { useTransactionContext } from '../../context/TransactionContext';
import moment from 'moment';
import TextArea from 'antd/es/input/TextArea';

const { RangePicker } = DatePicker;

const Calculator = () => {
  const { transactions, settransactions } = useTransactionContext();
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [filterRange, setFilterRange] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFetchData, setIsFetchData] = useState(false);

  const [allMembers, setAllMembers] = useState([]);

  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isBtnDiasabled, setIsBtnDiasabled] = useState(false);
  const [tasksData, setTasksData] = useState([]);

  const [form] = Form.useForm();

  const showModal = () => setIsModalOpen(true);
  const showFilterModal = () => setIsFilterModalOpen(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };
  const handleFilterCancel = () => setIsFilterModalOpen(false);

  const handleApplyFilter = () => {
    setFilterRange(dateRange);
    setIsFilterModalOpen(false);
  };

  const handleResetFilter = () => {
    setDateRange([]);
    setFilterRange([]);
  };

  const handleAdd = async (values) => {
    setIsBtnDiasabled(true);
    try {
      const newData = {
        ...values,
        date: values.date.format('YYYY-MM-DD'),
      };
  
      if (!values.hasOil) {
        delete newData.oilType;
        delete newData.oilLiters;
      }
  
      setIsFetchData(true);
      let response;
      if (editingTransaction) {
        response = await editTransaction(editingTransaction.id, newData);
      } else {
        response = await createTransaction(newData);
      }
  
      if (response.status === 200 || response.status === 201) {
        setIsModalOpen(false);
        setIsFetchData(false);
        form.resetFields();
        setEditingTransaction(null);
        getTransaction();
      }
    } catch(err) {
      console.log("Error", err);
    } finally {
      setIsBtnDiasabled(false);
    }
  };

  const onEdit = (data) => {
    setEditingTransaction(data);
    form.setFieldsValue({
      name: data.name,
      type: data.type || "",
      unitPrice: data.unitPrice,
      performedBy: data.performedBy ? data.performedBy.id : data.performedByName ? data.performedByName : "",
      description: data.description || "",
      workerSalary: data.workerSalary,
      date: moment(data.date, 'YYYY-MM-DD'),
    });
    
    setIsModalOpen(true);
  }

  const onDelete = (id) => {
    Modal.confirm({
      title: 'Դուք համոզվա՞ծ եք',
      okText: 'Այո',
      okType: 'danger',
      cancelText: 'Ոչ',
      onOk: async () => {
        try {
          const result = await deleteTransaction(id); // Implement the delete API call here
          if (result.status === 200) {
            // Refresh or update the transaction list after successful deletion
            settransactions(prevTransactions => prevTransactions.filter(item => item.id !== id));
          }
        } catch (error) {
          console.error('Error deleting transaction:', error);
        }
      },
      onCancel() {
        console.log('Delete operation canceled');
      },
    });
  };

  const columns = [
    {
      title: '№',
      dataIndex: 'index',
      key: 'index',
      render: (text, record, index) => (page - 1) * limit + index + 1,
    },
    {
      title: 'Անվանում',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span>
          {text} {record.hasOil ? '(OIL)' : ''}
        </span>
      ),
    },
    {
      title: 'Տեսակ',
      dataIndex: 'type',
      key: 'type',
      render: (text) => (text ? text : '—'),
    },
    {
      title: 'Գումար',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (text) => `${text} դրամ`,
    },
    {
      title: 'Աշխատավարձ',
      dataIndex: 'workerSalary',
      key: 'workerSalary',
      render: (text) => `${text} դրամ`,
    },
    {
      title: 'Ով է կատարել',
      dataIndex: 'performedBy',
      key: 'performedBy',
      render: (_, record) => {
        if (record.performedBy && record.performedBy.name) {
          return record.performedBy.name;
        } else if (record.performedByName) {
          return `${record.performedByName} (Նախկին աշխատող)`
        } else {
          return '—';
        }
      },
    },
    {
      title: 'Ամսաթիվ',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Հավելյալ նշումներ',
      dataIndex: 'description',
      key: 'description',
      render: (text) => (text ? text : '—'),
    },
    {
      title: "Գործողություններ",
      key: "actions",
      align: "center",
      render: (_, record) => {
        const menuItems = [
          {
            key: "edit",
            label: "✏️ Խմբագրել",
            onClick: () => onEdit(record),
          },
          {
            key: "delete",
            label: "🗑️ Ջնջել",
            danger: true,
            onClick: () => onDelete(record.id),
          },
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const getTransaction = async () => {
    setLoading(true);
    try {
      const data = {
        search,
        startDate: dateRange.length ? dateRange[0].format("YYYY-MM-DD") : "",
        endDate: dateRange.length ? dateRange[1].format("YYYY-MM-DD") : "",
        page,
        limit
      }
      const allData = await getAllTransactions(data);
      const all_transactions = allData.data;
      if (allData.status === 200) {
        settransactions(all_transactions?.data);
        setTotal(all_transactions.total);
        if (all_transactions.data.length === 0 && !search) {
          setDisabled(true)
        } else {
          setDisabled(false)
        }
      }
    } catch(err) {
      console.log("Error", err)
    } finally {
      setLoading(false);
    }
  };

  const works = async () => {
    setIsModalLoading(true);
    try {
      const tasks = await getWorksByTransactionsData();
      if (tasks.status === 200) {
        setTasksData(tasks?.data?.data)
      }
    } catch(err) {
      console.log("Error", err)
    } finally{
      setIsModalLoading(false);
    }
  }

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    if (e.target.value === "") {
      getTransaction();
    }
  };

  const allMembersData = async () => {
        const allMembersData = await getMembersData();        
        const data = allMembersData.data;
        if (allMembersData.status === 200) {
          setAllMembers(data);
        }
  };

  const handleTaskChange = (taskId) => {
    const selectedTask = tasksData.find((task) => task.id === taskId);
    
    if (selectedTask) {
      form.setFieldsValue({ unitPrice: Number( selectedTask.totalAmount )});
    }
  };
  
  useEffect(() => {
      allMembersData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (search) {
      const delayDebounceFn = setTimeout(() => {
        getTransaction();
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    } else {
      getTransaction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, filterRange, isFetchData]);

  useEffect(() => {
    if (isModalOpen) {
      works();
    }
  }, [isModalOpen])
  
  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <Input
          placeholder="Որոնել..."
          prefix={<SearchOutlined />}
          disabled={disabled}
          value={search}
          onChange={handleSearchChange}
          allowClear
          style={{ width: 200 }}
        />
        <Button icon={<FilterOutlined />} onClick={showFilterModal}>
          Ֆիլտր
        </Button>
        {filterRange.length > 0 && (
          <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>
            Մաքրել ֆիլտրը
          </Button>
        )}
        <Button type="primary" onClick={showModal}>
          Ավելացնել
        </Button>
      </div>

      <Modal title="Ֆիլտր" open={isFilterModalOpen} onCancel={handleFilterCancel} footer={null}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <RangePicker format="YYYY-MM-DD" value={dateRange.length ? dateRange : null} onChange={(dates) => setDateRange(dates || [])} placeholder={["Սկիզբ", "Վերջ"]} />
          <Button type="primary" onClick={handleApplyFilter} block>
            Հաստատել
          </Button>
        </div>
      </Modal>

      <Table
        columns={columns}
        dataSource={transactions}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: limit,
          total: total
        }}
        loading={loading}
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
      />

      <Modal
        title="Ավելացնել Տվյալներ"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
      >
        {isModalLoading ? <Spin style={{
          width: "100%", 
          height: "400px", 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center"
          }} /> : <Form layout="vertical" onFinish={handleAdd} form={form}>
          <Form.Item
            label="Անվանում"
            name="name"
            rules={[{ required: true, message: 'Խնդրում ենք լրացնել անվանումը' }]}
          >
            <Input placeholder="Օրինակ՝ Մեքենայի լվացում" />
          </Form.Item>
          <Form.Item
            label="Տեսակ"
            name="type"
          >
            <Input placeholder="Օրինակ՝ Ծառայություն, Ապրանք" />
          </Form.Item>
          <Form.Item
            label="Ով է կատարել"
            name="performedBy"
          >
            <Select
                  showSearch
                  placeholder="Ընտրել կատարողին"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                      option?.children.toLowerCase().includes(input.toLowerCase())
                  }
              >
                  {allMembers?.map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                          {item.name}
                      </Select.Option>
                  ))}
              </Select>
          </Form.Item>
          <Form.Item
              label="Աշխատանքը"
              name="taskId"
          >
              <Select 
                  showSearch
                  placeholder="Ընտրել աշխատանքը"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                      option?.children.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleTaskChange}
              >
                  {tasksData?.map((task, index) => (
                      <Select.Option key={index} value={task.id}>
                          {task.work_name[0]} <span style={{color: "red"}}>({task.car_number})</span>
                      </Select.Option>
                  ))}
              </Select>
          </Form.Item>
          <Form.Item
            label="Ընդհանուր Գումար"
            name="unitPrice"
            rules={[{ required: true, message: 'Խնդրում ենք լրացնել գումարը' }]}
          >
            <Input type="number" placeholder="Օրինակ՝ 5000" />
          </Form.Item>
          <Form.Item
            label="Աշխատողի Աշխատավարձ"
            name="workerSalary"
            rules={[{ required: true, message: 'Խնդրում ենք լրացնել աշխատավարձը' }]}
          >
            <Input type="number" placeholder="Օրինակ՝ 3000" />
          </Form.Item>
          <Form.Item
            label="Ամսաթիվ"
            name="date"
            rules={[{ required: true, message: 'Խնդրում ենք լրացնել ամսաթիվը' }]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              placeholder="Օրինակ՝ 2025-01-27"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
              name="description"
              label="Հավելյալ նշումներ"
          >
              <TextArea 
                  autoSize={{ minRows: 3, maxRows: 5 }} 
                  placeholder="Մուտքագրեք ավելին..." 
              />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block disabled={isBtnDiasabled}>
              Ավելացնել
            </Button>
          </Form.Item>
        </Form>}
      </Modal>
    </div>
  );
};

export default Calculator;
